import React, { useEffect, useMemo, useState } from 'react';
import { simulationApi } from '../services/simulationApi';
import { api } from '../../../api/client';

type ProfessorOpt = { id: string; nome: string; email: string | null; fotoUrl?: string | null };
type ResponsavelOpt = {
  id: string;
  responsavelId: string;
  nome: string;
  email: string;
  fotoUrl?: string | null;
  alunoId: string;
  alunoNome: string;
};
type TurmaOpt = {
  id: string;
  nome: string;
  serie: string;
  turno: string;
  responsaveis: ResponsavelOpt[];
};

export type SchoolEmbedMobileHeaderProps = {
  schoolId: string;
  role: 'PROFESSOR' | 'RESPONSAVEL';
  professorId?: string;
  responsavelId?: string;
  showProfessorSelect: boolean;
  showResponsavelSelect: boolean;
  toolsMenuOpen: boolean;
  onToggleTools: () => void;
  onHide: () => void;
  onProfessorChange: (professorId: string) => void;
  onResponsavelChange: (responsavelId: string) => void;
};

function persistActorSession(
  schoolId: string,
  roleKey: 'PROFESSOR' | 'RESPONSAVEL',
  actorId: string,
  sessionData: any,
) {
  const tagged = {
    ...sessionData,
    schoolId,
    role: roleKey,
    user: sessionData.user || { id: actorId, perfil: roleKey },
  };
  const sessions = JSON.parse(localStorage.getItem('ac_sessions') || '{}');
  sessions[actorId] = tagged;
  sessions[roleKey] = tagged;
  sessions[`${schoolId}::${roleKey}`] = tagged;
  localStorage.setItem('ac_sessions', JSON.stringify(sessions));
  return tagged;
}

export async function connectSchoolRoleActor(
  schoolId: string,
  roleKey: 'PROFESSOR' | 'RESPONSAVEL',
  actorId: string,
): Promise<boolean> {
  try {
    const existing = JSON.parse(localStorage.getItem('ac_sessions') || '{}');
    const cached =
      existing[actorId] ||
      existing[`${schoolId}::${roleKey}`];
    if (
      cached?.access_token &&
      cached.user?.id === actorId &&
      (!cached.schoolId || cached.schoolId === schoolId)
    ) {
      persistActorSession(schoolId, roleKey, actorId, cached);
      return true;
    }

    const res = await api.post('/simulation/start', {
      escolaId: schoolId,
      actorIds: [actorId],
    });
    const bag = res.data?.sessions || res.data || {};
    const sessionData =
      bag[roleKey] ||
      bag[actorId] ||
      Object.values(bag).find((s: any) => s?.user?.id === actorId);
    if (!sessionData || !(sessionData as any).access_token) return false;
    persistActorSession(schoolId, roleKey, actorId, sessionData);
    return true;
  } catch (err) {
    console.error('[SchoolEmbedMobileHeader] connectSchoolRoleActor', err);
    return false;
  }
}

export const SchoolEmbedMobileHeader: React.FC<SchoolEmbedMobileHeaderProps> = ({
  schoolId,
  role,
  professorId,
  responsavelId,
  showProfessorSelect,
  showResponsavelSelect,
  toolsMenuOpen,
  onToggleTools,
  onHide,
  onProfessorChange,
  onResponsavelChange,
}) => {
  const [loading, setLoading] = useState(false);
  const [professors, setProfessors] = useState<ProfessorOpt[]>([]);
  const [turmas, setTurmas] = useState<TurmaOpt[]>([]);
  const [busy, setBusy] = useState(false);

  // Resolve professor da sessão da escola se a janela ainda não guardou roleActors
  const resolvedProfessorId = useMemo(() => {
    if (professorId) return professorId;
    try {
      const sessions = JSON.parse(localStorage.getItem('ac_sessions') || '{}');
      return (
        sessions[`${schoolId}::PROFESSOR`]?.user?.id ||
        sessions.PROFESSOR?.user?.id ||
        undefined
      );
    } catch {
      return undefined;
    }
  }, [professorId, schoolId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await simulationApi.getCockpitMobileOptions(schoolId, resolvedProfessorId);
        if (cancelled) return;
        setProfessors(data.professors || []);
        setTurmas(data.turmas || []);

        // Se o painel RESP ainda não tem ator, auto-seleciona o primeiro elegível
        const flat = (data.turmas || []).flatMap((t) => t.responsaveis);
        if (!responsavelId && flat[0]?.id) {
          const pick =
            flat.find((r) => r.id.startsWith('00000001-')) || flat[0];
          const ok = await connectSchoolRoleActor(schoolId, 'RESPONSAVEL', pick.id);
          if (ok && !cancelled) onResponsavelChange(pick.id);
        }
      } catch (err) {
        console.warn('[SchoolEmbedMobileHeader] options', err);
        if (!cancelled) {
          setProfessors([]);
          setTurmas([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onResponsavelChange é setter estável do pai
  }, [schoolId, resolvedProfessorId, responsavelId]);

  const selectedProfessorId = resolvedProfessorId || professors[0]?.id || '';
  const flatResponsaveis = useMemo(
    () => turmas.flatMap((t) => t.responsaveis.map((r) => ({ ...r, turmaNome: t.nome }))),
    [turmas],
  );
  const selectedResponsavelId =
    responsavelId && flatResponsaveis.some((r) => r.id === responsavelId)
      ? responsavelId
      : flatResponsaveis[0]?.id || '';

  const handleProfessorSelect = async (nextId: string) => {
    if (!nextId || nextId === selectedProfessorId || busy) return;
    setBusy(true);
    try {
      const ok = await connectSchoolRoleActor(schoolId, 'PROFESSOR', nextId);
      if (!ok) return;
      onProfessorChange(nextId);
      // Re-pareia responsável da nova grade
      try {
        const paired = await simulationApi.getPairedResponsavel(schoolId, nextId);
        if (paired?.found && paired.responsavel?.id) {
          await connectSchoolRoleActor(schoolId, 'RESPONSAVEL', paired.responsavel.id);
          onResponsavelChange(paired.responsavel.id);
        } else {
          onResponsavelChange('');
        }
      } catch {
        onResponsavelChange('');
      }
    } finally {
      setBusy(false);
    }
  };

  const handleResponsavelSelect = async (nextId: string) => {
    if (!nextId || nextId === selectedResponsavelId || busy) return;
    setBusy(true);
    try {
      const ok = await connectSchoolRoleActor(schoolId, 'RESPONSAVEL', nextId);
      if (ok) onResponsavelChange(nextId);
    } finally {
      setBusy(false);
    }
  };

  const title = role === 'PROFESSOR' ? 'Celular · Professor' : 'Celular · Responsável';

  return (
    <div className="px-2 py-1.5 border-b border-[#313244]/80 flex items-center gap-1.5 shrink-0 min-h-[36px]">
      <button
        type="button"
        className={`w-6 h-6 shrink-0 rounded hover:bg-[#313244]/70 text-[#cdd6f4] hover:text-white flex items-center justify-center transition-all active:scale-90 cursor-pointer bg-transparent border-none ${
          toolsMenuOpen ? 'text-[#89b4fa]' : ''
        }`}
        title="Ferramentas do celular"
        aria-label="Menu de ferramentas"
        onClick={onToggleTools}
      >
        <svg className="w-3.5 h-3.5 fill-none stroke-current stroke-2" viewBox="0 0 24 24">
          <line x1="3" y1="12" x2="21" y2="12" />
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="3" y1="18" x2="21" y2="18" />
        </svg>
      </button>

      {showProfessorSelect ? (
        <select
          className="min-w-0 flex-1 max-w-[46%] bg-[#181825] border border-[#313244] rounded-md text-[9px] font-bold text-[#cdd6f4] px-1.5 py-1 outline-none cursor-pointer disabled:opacity-50"
          title="Professor da escola"
          disabled={loading || busy || professors.length === 0}
          value={selectedProfessorId}
          onChange={(e) => void handleProfessorSelect(e.target.value)}
        >
          {professors.length === 0 ? (
            <option value="">Sem professores</option>
          ) : (
            professors.map((p) => (
              <option key={p.id} value={p.id}>
                {p.nome}
              </option>
            ))
          )}
        </select>
      ) : (
        <span className="text-[9px] font-extrabold tracking-wider uppercase text-[#89b4fa] truncate shrink-0">
          {title}
        </span>
      )}

      <div className="flex-1 min-w-1" />

      {showResponsavelSelect && (
        <select
          className="min-w-0 flex-1 max-w-[46%] bg-[#181825] border border-[#313244] rounded-md text-[9px] font-bold text-[#cdd6f4] px-1.5 py-1 outline-none cursor-pointer disabled:opacity-50"
          title="Responsável das turmas do professor"
          disabled={loading || busy || flatResponsaveis.length === 0}
          value={selectedResponsavelId}
          onChange={(e) => void handleResponsavelSelect(e.target.value)}
        >
          {loading ? (
            <option value="">Carregando responsáveis…</option>
          ) : flatResponsaveis.length === 0 ? (
            <option value="">Sem responsáveis com login nas turmas</option>
          ) : (
            turmas.map((t) =>
              t.responsaveis.length === 0 ? null : (
                <optgroup key={t.id} label={`${t.nome}${t.serie ? ` · ${t.serie}` : ''}`}>
                  {t.responsaveis.map((r) => (
                    <option key={`${t.id}-${r.id}`} value={r.id}>
                      {r.nome} · {r.alunoNome}
                    </option>
                  ))}
                </optgroup>
              ),
            )
          )}
        </select>
      )}

      <button
        type="button"
        className="text-[10px] text-[#a6adc8] hover:text-white bg-transparent border-none cursor-pointer px-1 shrink-0"
        title="Ocultar este celular"
        onClick={onHide}
      >
        ✕
      </button>
    </div>
  );
};
